/**
 * Epoch millis → a weekday, date, and time of day in the given locale (FR-039).
 *
 * `Intl.DateTimeFormat` rather than a hand-rolled format string, because FR-039's "consistent with the
 * user's locale" covers field *order* and separators as much as month names — `fr-CA` puts the day
 * before the month, and no template literal gets that right for three locales at once.
 *
 * **The locale is a parameter, not read from `Locale` here.** That keeps this a pure function testable
 * across all three locales without mutating global state, which matters because `Locale.change` is
 * process-wide and would leak between tests. `LoginHistoryTab` passes `Locale.getCurrent()`.
 *
 * The times are rendered in the **viewer's** timezone, which is what a user checking their own sign-ins
 * wants — CC9 sends an absolute instant and takes no position on zone.
 */
const FORMAT: Intl.DateTimeFormatOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit"
};

/** Rendered in place of an unusable timestamp. */
const NOT_AVAILABLE = "—";

export const formatLoginDate = (
  epochMillis: number,
  locale: string
): string => {
  /*
   * `loginHistory` is declared `number[]`, but nothing validates the payload at runtime — the single
   * unvalidated cast lives in `configurationRequest`. Left unguarded, a `NaN` or `Infinity` renders as
   * the literal string "Invalid Date" in the middle of the user's sign-in list, which reads like real
   * data. An em dash is unambiguous. `0` is deliberately **not** filtered: the epoch is a legitimate
   * instant, and `!epochMillis` would have discarded it.
   */
  if (!Number.isFinite(epochMillis)) {
    return NOT_AVAILABLE;
  }

  return new Intl.DateTimeFormat(locale, FORMAT).format(new Date(epochMillis));
};
